import { useState } from "react";
import Cropper, { type Area, type Point } from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { FlipHorizontal, FlipVertical, RotateCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { SwatchRow } from "@/components/deck/SwatchRow";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import {
  cssFilter,
  FILTER_PRESETS,
  filterPresetId,
  IMAGE_BACKGROUND_DEFAULT,
  imageBackground,
} from "@/lib/image";
import { sheetSwatches } from "@/lib/stylesheet-typography";
import { useAppSelector } from "@/store/hooks";
import { selectEffectiveStyleSheet } from "@/store/styleDraftSlice";
import { cn } from "@/lib/utils";
import type { ImageCrop, ImageFilters, ImageTransform } from "@/types/content";

// Crop / zoom / rotate / flip editor (TODO §3.4), react-easy-crop under the hood. It edits
// *data* only — croppedAreaPixels + a quarter-turn + flips — which ImageBlock re-applies
// at render, so every edit is non-destructive and re-editable (§3.3).
//
// The cropper stays upright (rotation 0); the Rotate button sets a decoupled quarter-turn
// that ImageBlock applies after the crop. That keeps the render pure-CSS and correct; the
// only cost is the in-dialog cropper doesn't preview the post-crop rotation.
//
// §22.2 It is also the SVG editor now, in a reduced form: a vector has no crop, no zoom and
// no quarter-turn to set (ImageBlock's SVG branch applies none of them), but it does have a
// background and a filter, and those two controls had nowhere else to live. `isSvg` swaps the
// cropper for a plain preview and hides the controls that would write data nothing reads.

// `null` = the image's own aspect, i.e. "don't crop". It is first and it is the default for an
// image that has never been cropped (B4): the dialog is also the only way to reach the filter
// and rotate controls, so defaulting to 16:9 meant opening it to apply a grayscale and pressing
// Save silently cropped the picture — and, under B4's rule, flipped it from fit to fill.
const ASPECTS: { label: string; value: number | null }[] = [
  { label: "Whole image", value: null },
  { label: "16:9", value: 16 / 9 },
  { label: "4:3", value: 4 / 3 },
  { label: "3:2", value: 3 / 2 },
  { label: "1:1", value: 1 },
];

// `onCropComplete`'s first argument is the crop in PERCENT of the image, so "the user did not
// actually crop anything" is exactly a full-size rectangle at the origin — no natural size and
// no pixel epsilon needed. Such a crop is stored as `undefined`, which is what keeps
// `effectiveFit` reading it as an uncropped image.
function isWholeImage(area: Area | null): boolean {
  return (
    area != null &&
    area.x <= 0.01 &&
    area.y <= 0.01 &&
    area.width >= 99.99 &&
    area.height >= 99.99
  );
}

export function ImageEditDialog({
  open,
  onOpenChange,
  src,
  isSvg = false,
  initialCrop,
  initialTransform,
  initialFilters,
  initialBackground,
  onSave,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  src: string;
  // §22.2 A vector: no crop/zoom/rotate/flip, background and filter only.
  isSvg?: boolean;
  initialCrop?: ImageCrop;
  initialTransform?: ImageTransform;
  initialFilters?: ImageFilters;
  initialBackground?: string;
  onSave: (patch: {
    // Absent when the rectangle covers the whole image — an uncropped image, which
    // `effectiveFit` renders whole instead of filling the window (B4).
    crop?: ImageCrop;
    transform: ImageTransform;
    filters?: ImageFilters;
    // §22.2 A stylesheet colour NAME, or undefined for the default white.
    background?: string;
  }) => void;
}) {
  const [cropPos, setCropPos] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  // An already-cropped image opens on 16:9 as before; an uncropped one opens on "Whole image".
  const [aspect, setAspect] = useState<number | null>(
    initialCrop ? 16 / 9 : null,
  );
  // Natural size, needed only to turn "Whole image" into a concrete aspect for the cropper.
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [rotation, setRotation] = useState<0 | 90 | 180 | 270>(
    initialTransform?.rotation ?? 0,
  );
  const [flipH, setFlipH] = useState(initialTransform?.flipH ?? false);
  const [flipV, setFlipV] = useState(initialTransform?.flipV ?? false);
  const [filterId, setFilterId] = useState(() =>
    filterPresetId(initialFilters),
  );
  // §22.2 The window's background, as a colour NAME (undefined = default white). The swatches
  // come from the EFFECTIVE sheet, so a live stylesheet preview offers the colours the user is
  // currently looking at — the same source every other swatch picker in the app reads.
  const [background, setBackground] = useState<string | undefined>(
    initialBackground,
  );
  const effectiveStyleSheet = useAppSelector(selectEffectiveStyleSheet);
  const swatches = sheetSwatches(effectiveStyleSheet);
  const backgroundValue = imageBackground(effectiveStyleSheet, { background });

  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  // The same rectangle in percent — the signal for "nothing was cropped" (see isWholeImage).
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);

  const filters = FILTER_PRESETS.find((p) => p.id === filterId)?.filters ?? {};
  const previewFilter = cssFilter(filters);

  const save = () => {
    // Fall back to the seeded crop if the user never moved the cropper (no onCropComplete
    // fired). A rectangle covering the whole image is stored as *no* crop, so Save always
    // writes what the user actually did — and never invents a crop for someone who opened
    // this dialog only to rotate or filter (B4).
    const crop = isWholeImage(croppedArea)
      ? undefined
      : (croppedAreaPixels ?? initialCrop);
    onSave({
      // §22.2 A vector never writes a crop or a transform of its own — the SVG branch of
      // ImageBlock reads neither, so writing one would be invisible data that a later
      // `effectiveFit` change could suddenly start honouring. Whatever was already stored is
      // passed straight back through.
      crop: isSvg ? initialCrop : crop,
      transform: isSvg ? (initialTransform ?? {}) : { rotation, flipH, flipV },
      // Store undefined (not {}) when there's no filter, so "None" clears cleanly.
      filters: previewFilter ? filters : undefined,
      background,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit image</DialogTitle>
        </DialogHeader>

        <div className="relative h-[360px] w-full overflow-hidden rounded-md bg-slate-900">
          {isSvg ? (
            // §22.2 No cropper for a vector — just the picture as the slide will show it,
            // on the chosen background, so the swatch row below has something to preview
            // against. `contain` matches ImageBlock's SVG branch.
            <img
              src={src}
              alt=""
              className="h-full w-full"
              style={{
                objectFit: "contain",
                filter: previewFilter,
                backgroundColor: backgroundValue,
              }}
            />
          ) : (
            <Cropper
              image={src}
              crop={cropPos}
              zoom={zoom}
              // "Whole image" has no fixed ratio of its own — it is the image's, which makes
              // the crop box the entire picture at zoom 1.
              aspect={aspect ?? (natural ? natural.w / natural.h : 16 / 9)}
              // Keep the crop within the image (react-easy-crop's default). With this off, a
              // crop could be panned into the empty area past the image edge; that stored
              // out-of-bounds rectangle then renders shifted/gapped in the slide. placeImage
              // also clamps at render as a backstop for any already-saved crop.
              restrictPosition={true}
              initialCroppedAreaPixels={initialCrop}
              onMediaLoaded={({ naturalWidth, naturalHeight }) =>
                setNatural({ w: naturalWidth, h: naturalHeight })
              }
              onCropChange={setCropPos}
              onZoomChange={setZoom}
              onCropComplete={(area, pixels) => {
                setCroppedArea(area);
                setCroppedAreaPixels(pixels);
              }}
              // Live filter preview on the media only (crop UI stays unfiltered). §22.2 The
              // background rides along on the media box so a transparent PNG previews against
              // the colour it will actually sit on rather than against the cropper's dark mat.
              style={{
                mediaStyle: {
                  filter: previewFilter,
                  backgroundColor: backgroundValue,
                },
              }}
            />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {!isSvg && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">Aspect</span>
              {ASPECTS.map((a) => (
                <button
                  key={a.label}
                  type="button"
                  onClick={() => {
                    setAspect(a.value);
                    // Re-fit: a ratio change with the view still zoomed in leaves a crop the
                    // new button does not describe.
                    setZoom(1);
                    setCropPos({ x: 0, y: 0 });
                  }}
                  className={cn(
                    "rounded border px-2 py-1 text-xs",
                    aspect === a.value
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 text-slate-600 hover:bg-slate-100",
                  )}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}

          {!isSvg && (
            <div className="flex min-w-[160px] flex-1 items-center gap-2">
              <span className="text-xs font-medium text-slate-500">Zoom</span>
              <Slider
                min={1}
                max={3}
                step={0.01}
                value={[zoom]}
                onValueChange={([v]) => setZoom(v)}
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-500">Filter</span>
            <Select value={filterId} onValueChange={setFilterId}>
              <SelectTrigger className="h-8 w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTER_PRESETS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!isSvg && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setRotation((r) => ((r + 90) % 360) as 0 | 90 | 180 | 270)
                }
                aria-label="Rotate 90 degrees"
              >
                <RotateCw className="mr-2 h-4 w-4" /> {rotation}°
              </Button>
              <Button
                variant={flipH ? "default" : "outline"}
                size="sm"
                onClick={() => setFlipH((f) => !f)}
                aria-label="Flip horizontal"
                aria-pressed={flipH}
              >
                <FlipHorizontal className="h-4 w-4" />
              </Button>
              <Button
                variant={flipV ? "default" : "outline"}
                size="sm"
                onClick={() => setFlipV((f) => !f)}
                aria-label="Flip vertical"
                aria-pressed={flipV}
              >
                <FlipVertical className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* §22.2 What the window paints where the picture does not reach — the letterbox bars
            of an uncropped image, and whatever a transparent PNG or SVG lets through. NAMES,
            not hexes (see `SwatchRow`), so a sheet swap recolours these windows too. */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Background</span>
          <SwatchRow
            swatches={swatches}
            value={background}
            onPick={setBackground}
            inheritLabel="Slide"
            inheritColor={IMAGE_BACKGROUND_DEFAULT}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
